/**
 * InvestorNotesSection
 *
 * Compact always-visible notepad strip for capturing notes during investor calls.
 *
 * UX: sits between the investor header and the EOI/Fund Update tabs. Multiline
 * textarea with an explicit Save button (Ctrl+Enter shortcut). Recent notes scroll
 * in a fixed-height box below — never pushes the tabs out of view.
 */

import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { Box, CircularProgress, IconButton, InputBase, Tooltip, Typography } from '@mui/material';
import BackendService, { AdminNoteItem } from 'BackendService';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const PAGE_SIZE = 20;

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);
  if (diffMins < 2) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface Props {
  investorExternalId: string;
}

export default function InvestorNotesSection({ investorExternalId }: Props) {
  const [notes, setNotes] = useState<AdminNoteItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [draft, setDraft] = useState('');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bs = useCallback(() => new BackendService(), []);

  useEffect(() => {
    bs()
      .listAdminNotes('INVESTOR', investorExternalId, { limit: PAGE_SIZE, offset: 0 })
      .then((res) => {
        setNotes(res.data);
        setHasMore(res.data.length === PAGE_SIZE);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [investorExternalId, bs]);

  // Clear the "saved" flash timer on unmount
  useEffect(
    () => () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    },
    [],
  );

  const loadMore = () => {
    setLoadingMore(true);
    bs()
      .listAdminNotes('INVESTOR', investorExternalId, { limit: PAGE_SIZE, offset: notes.length })
      .then((res) => {
        setNotes((p) => [...p, ...res.data]);
        setHasMore(res.data.length === PAGE_SIZE);
      })
      .catch(console.error)
      .finally(() => setLoadingMore(false));
  };

  // Explicit save — triggered by button click or Ctrl+Enter
  const handleSave = async () => {
    const content = draft.trim();
    if (!content || saveStatus === 'saving') return;
    setSaveStatus('saving');
    try {
      await bs().createAdminNote({
        modelType: 'INVESTOR',
        modelExternalId: investorExternalId,
        content,
      });
      const res = await bs().listAdminNotes('INVESTOR', investorExternalId, {
        limit: Math.max(notes.length + 1, PAGE_SIZE),
        offset: 0,
      });
      setNotes(res.data);
      setHasMore(res.data.length === PAGE_SIZE);
      setDraft('');
      setSaveStatus('saved');
      savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Failed to save note', err);
      setSaveStatus('error');
      savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSave();
    }
  };

  const remove = async (externalId: string) => {
    await bs().deleteAdminNote(externalId).catch(console.error);
    setNotes((p) => p.filter((n) => n.externalId !== externalId));
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Input area */}
      <Box
        sx={{
          px: 1.5,
          pt: 0.75,
          pb: 0.5,
          borderBottom: notes.length > 0 || loading ? '1px solid' : 'none',
          borderColor: 'divider',
        }}
      >
        <InputBase
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a note…"
          multiline
          minRows={2}
          maxRows={6}
          fullWidth
          disabled={saveStatus === 'saving'}
          sx={{ fontSize: '0.875rem', alignItems: 'flex-start' }}
        />
        {/* Save row: button right-aligned, status left */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 24,
            pb: 0.25,
          }}
        >
          <Typography
            variant="caption"
            color={
              saveStatus === 'error'
                ? 'error'
                : saveStatus === 'saved'
                  ? 'success.main'
                  : 'text.disabled'
            }
          >
            {saveStatus === 'saving' && (
              <CircularProgress size={10} sx={{ mr: 0.5, verticalAlign: 'middle' }} />
            )}
            {saveStatus === 'saving'
              ? 'Saving…'
              : saveStatus === 'saved'
                ? '✓ Saved'
                : saveStatus === 'error'
                  ? 'Save failed'
                  : ''}
          </Typography>
          <Tooltip title="Save note (Ctrl+Enter)">
            <span>
              <IconButton
                size="small"
                onClick={handleSave}
                disabled={!draft.trim() || saveStatus === 'saving'}
                color="primary"
                aria-label="Save note"
              >
                {saveStatus === 'saving' ? (
                  <CircularProgress size={16} />
                ) : (
                  <SaveIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Notes history */}
      {(loading || notes.length > 0) && (
        <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={1.5}>
              <CircularProgress size={18} />
            </Box>
          ) : (
            notes.map((note) => (
              <Box
                key={note.externalId}
                sx={{
                  display: 'flex',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  alignItems: 'flex-start',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: hasMore ? '1px solid' : 'none' },
                  '&:hover .note-delete': { opacity: 1 },
                }}
              >
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ whiteSpace: 'nowrap', pt: 0.25, minWidth: 52 }}
                  title={new Date(note.createdAt).toLocaleString('en-AU')}
                >
                  {formatDate(note.createdAt)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}
                >
                  {note.content}
                </Typography>
                <IconButton
                  className="note-delete"
                  size="small"
                  onClick={() => remove(note.externalId)}
                  sx={{ opacity: 0, transition: 'opacity 0.15s', p: 0.25, flexShrink: 0 }}
                  title="Delete note"
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))
          )}
          {hasMore && (
            <Box
              component="button"
              onClick={loadMore}
              disabled={loadingMore}
              sx={{
                display: 'block',
                width: '100%',
                px: 1.5,
                py: 0.5,
                border: 'none',
                bgcolor: 'transparent',
                cursor: 'pointer',
                color: 'text.secondary',
                fontSize: '0.75rem',
                textAlign: 'left',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
